import React, { useContext, useEffect, useState } from "react";
import DefaultButton from "../../styles/materialButtonStyle";
import MaterialDialog from "../../styles/materialDialog";
import { TransferContainerContext } from "../../transfer/client/TransferContainer";
import ServiceRelationshipsSelectForTransfer from "./ServiceRelationshipsSelectForTransfer";
import { TextField } from "@material-ui/core";
import { StyledSelect, StyledSelectLabel } from "../../styles/styledSelect";
import CreatableSelect from "react-select/creatable";
import { check2FA } from "../../../utils/check2FA";
import getUserInfo from "../../../utils/getUserInfo";
import { isEmptyCollectionValue } from "../../../utils/collections";
import axios from "axios";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import { useTranslation } from "react-i18next";
import { HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../utils/consts";

import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../styles/styledDialog";
import { parseAxiosError } from "../../../utils/response";

const TransferDialog = ({ refetch, setRefetch }) => {
  const { t } = useTranslation("transferPage");

  const { notification, setNotification } = useContext(TransferContainerContext);

  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const defaultTransferData = {
    "service": null,
    "currency": null,
    "receiver": null,
    "amount": null,
    "note": null,
    "twofa": null
  };

  const [transferData, setTransferData] = useState(defaultTransferData);

  const [selectedOption, setSelectedOption] = useState(null);

  const [userEnabledEmails, setUserEnabledEmails] = useState(null);

  const handleChange = (selected) => {
    setSelectedOption(selected);

    setTransferData({
      ...transferData,
      "receiver": selected?.value
    });
  };

  const fetchEnabledUserEmails = () => {
    axios.get(`/api/transfers/recently`, userAuthenticationConfig()).then(response => {

      if (response.status === HTTP_STATUSES.HTTP_OK && response.data) {
        let tmpEmails = [];

        response.data.forEach(item => {
          tmpEmails.push({ label: item.email, value: item.email });
        });

        setUserEnabledEmails(tmpEmails);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const addOptionToSelect = (createdValueFromSelect) => {
    setSelectedOption({ label: createdValueFromSelect, value: createdValueFromSelect });

    setTransferData({
      ...transferData,
      "receiver": createdValueFromSelect
    });
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    setDisabled(true);

    let checkFields;

    checkFields = isEmptyCollectionValue(transferData, ["note"]);

    if (checkFields) {
      setNotification({ ...notification, visible: true, type: "error", message: t("transferDialog.checkFields") });
      setDisabled(false);

      return;
    }

    if (transferData.amount < 0) {
      setNotification({ ...notification, visible: true, type: "error", message: t("transferDialog.transferAmount") });
      setDisabled(false);

      return;
    }

    let check2Fa = await check2FA(getUserInfo()["userId"], transferData.twofa);

    if (check2Fa.error) {
      setNotification({ ...notification, visible: true, type: "error", message: check2Fa.error });
      setDisabled(false);

      return;
    }

    let data = {
      "service": transferData.service.id,
      "currency": transferData.currency.asset,
      "receiver": transferData.receiver,
      "amount": transferData.amount,
      "note": transferData.note
    };

    axios.post("/api/transfers", data, userAuthenticationConfig(false)).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {

        setRefetch(!refetch);
        setTransferData(defaultTransferData);
        setSelectedOption(null);
        setOpen(false);

        setNotification({ ...notification, visible: true, type: "success", message: t("transferDialog.success") });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
      setTransferData(defaultTransferData);
      setSelectedOption(null);
      setOpen(false);
    }).finally(() => setDisabled(false));
  };

  useEffect(() => {
    fetchEnabledUserEmails();
  }, []);

  return (
    <>
      <div className="create-transfer">
        <DefaultButton
          color="success"
          onClick={() => {
            setOpen(true);
          }}
        >
          {t("transferDialog.form.createTitle")}
        </DefaultButton>
      </div>

      <MaterialDialog open={open}>
        <StyledDialogWrapper>
          <StyledDialogHead>
            {t("transferDialog.form.createTitle")}
          </StyledDialogHead>
          <StyledDialogContent>
            <ServiceRelationshipsSelectForTransfer
              setCheckedServiceRelationships={(data) => setTransferData({
                ...transferData,
                "service": data.service,
                "currency": data.currency
              })}
            />
            <StyledSelect>
              <StyledSelectLabel>
                {t("transferDialog.form.recipientEmail")}
              </StyledSelectLabel>
              <CreatableSelect
                className="custom-creatable"
                classNamePrefix="custom-creatable"
                isClearable={true}
                value={selectedOption}
                onChange={handleChange}
                options={userEnabledEmails}
                onCreateOption={addOptionToSelect}
                placeholder={t("transferDialog.form.recipientEmail")}
                noOptionsMessage={() => t("transferDialog.form.noSavedEmails")}
                formatCreateLabel={(userInput) => `${t("transferDialog.form.add")}: ${userInput}`}
              />
            </StyledSelect>
            <TextField
              id="amount"
              label={t("transferDialog.form.amount")}
              value={transferData.amount}
              onChange={(data) => setTransferData(
                {
                  ...transferData,
                  "amount": data.target.value
                }
              )}
            />
            <TextField
              id="note"
              label={t("transferDialog.form.note")}
              value={transferData.note}
              onChange={(data) => setTransferData(
                {
                  ...transferData,
                  "note": data.target.value
                })}
            />
            <TextField
              id="twofa"
              label={t("transferDialog.form.twofa")}
              value={transferData.twofa}
              onChange={(data) => setTransferData({ ...transferData, "twofa": data.target.value })}
            />
          </StyledDialogContent>
          <StyledDialogAction col="2">
            <DefaultButton
              onClick={() => {
                setOpen(false);
              }}
              color="danger"
              disabled={disabled}
            >
              {t("transferDialog.form.cancel")}
            </DefaultButton>
            <DefaultButton
              color="success"
              onClick={handleCreate}
              disabled={disabled}
            >
              {t("transferDialog.form.createBtn")}
            </DefaultButton>
          </StyledDialogAction>
        </StyledDialogWrapper>
      </MaterialDialog>
    </>
  );
};

export default TransferDialog;